import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin, Compass, Navigation, Satellite, RefreshCw, MapPinned } from "lucide-react";
import { DeviceLocationMap } from "../DeviceLocationMap";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useQueryClient } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDeviceEditPermissions } from "@/hooks/use-device-edit-permissions";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { useDeviceChangeLog } from "@/hooks/use-device-change-log";
import { ManualLocationDialog } from "../ManualLocationDialog";

const convertNMEAToDecimal = (nmeaValue: number | string | null | undefined, direction?: string | null): number | null => {
  if (nmeaValue === null || nmeaValue === undefined || nmeaValue === "") return null;
  const str = nmeaValue.toString();
  const degrees = parseInt(str.slice(0, 2));
  const minutes = parseFloat(str.slice(2));
  let decimal = degrees + minutes / 60;
  if (direction === 'S') decimal = -decimal;
  return Number.isFinite(decimal) ? decimal : null;
};

const convertNMEALongitudeToDecimal = (nmeaValue: number | string | null | undefined, direction: string | null | undefined): number | null => {
  if (nmeaValue === null || nmeaValue === undefined || nmeaValue === "") return null;
  const str = nmeaValue.toString();
  const degrees = parseInt(str.slice(0, 3));
  const minutes = parseFloat(str.slice(3));
  let decimalDegrees = degrees + (minutes / 60);
  if (direction === 'W') {
    decimalDegrees = -decimalDegrees;
  }
  return Number.isFinite(decimalDegrees) ? decimalDegrees : null;
};

const parseCoordinate = (value: any): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const coordinate = typeof value === "number" ? value : Number(value);
  return Number.isFinite(coordinate) ? coordinate : null;
};

export const DeviceLocation = ({
  device
}: {
  device: any;
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  // Use location_enabled from database field
  const [isLocationEnabled, setIsLocationEnabled] = useState(
    Boolean(device?.location_enabled)
  );
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { canEditDeviceConfig } = useDeviceEditPermissions();
  const { logChange } = useDeviceChangeLog();
  const gnssLatitude = convertNMEAToDecimal(device.gnss?.latitude, device.gnss?.northSouth);
  const gnssLongitude = convertNMEALongitudeToDecimal(device.gnss?.longitude, device.gnss?.eastWest);
  const isValid = (v: any) =>
    typeof v === "number" && Number.isFinite(v);
  // Fall back to stored coordinates (e.g. manual location) when live GNSS is unavailable.
  const storedLatitude = parseCoordinate(device?.lat_wgs84 ?? device?.location?.latWGS84 ?? device?.location?.lat_wgs84);
  const storedLongitude = parseCoordinate(device?.long_wgs84 ?? device?.location?.longWGS84 ?? device?.location?.long_wgs84);
  const latitude = isValid(gnssLatitude)
    ? gnssLatitude
    : isValid(storedLatitude)
      ? storedLatitude
      : null;
  const longitude = isValid(gnssLongitude)
    ? gnssLongitude
    : isValid(storedLongitude)
      ? storedLongitude
      : null;

  const handleUpdateLocation = async () => {
    setIsUpdating(true);
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) throw new Error("No session found");
      
      const params = new URLSearchParams();
      params.append("deviceID", device.deviceID);
      
      const response = await fetch("https://i56o7w.buildship.run/updateLocation", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Authorization": `Bearer ${session.access_token}`
        },
        body: params
      });
      
      if (!response.ok) throw new Error("Failed to trigger location update");
      toast.success("Location update triggered");
    } catch (error) {
      console.error("Error triggering location update:", error);
      toast.error("Failed to trigger location update");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleLocation = async (enabled: boolean) => {
    setIsLocationEnabled(enabled);
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) throw new Error("No session found");
      
      // First API call: Update cameraConfig.enableGPS
      const gpsParams = new URLSearchParams();
      gpsParams.append("deviceID", device.deviceID);
      gpsParams.append("field", "cameraConfig.enableGPS");
      gpsParams.append("value", enabled ? "1" : "0");
      
      console.log("Updating GPS setting:", {
        deviceID: device.deviceID,
        enabled
      });
      
      logChange(device.deviceID, "cameraConfig.enableGPS", enabled ? "1" : "0");
      const gpsResponse = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Authorization": `Bearer ${session.access_token}`
        },
        body: gpsParams
      });
      
      if (!gpsResponse.ok) {
        const errorData = await gpsResponse.json();
        console.error("Error updating GPS setting:", errorData);
        throw new Error(errorData.message || "Failed to update GPS setting");
      }

      // Second API call: Update enableLocation
      const locationParams = new URLSearchParams();
      locationParams.append("deviceID", device.deviceID);
      locationParams.append("field", "enableLocation");
      locationParams.append("value", enabled ? "1" : "0");
      
      console.log("Updating enableLocation setting:", {
        deviceID: device.deviceID,
        enabled
      });
      
      logChange(device.deviceID, "enableLocation", enabled ? "1" : "0");
      const locationResponse = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Authorization": `Bearer ${session.access_token}`
        },
        body: locationParams
      });
      
      if (!locationResponse.ok) {
        const errorData = await locationResponse.json();
        console.error("Error updating enableLocation setting:", errorData);
        throw new Error(errorData.message || "Failed to update location setting");
      }

      // Refresh device data after both calls succeed
      queryClient.invalidateQueries({
        queryKey: ["device-details", device.deviceID]
      });
      queryClient.invalidateQueries({
        queryKey: ["device", device.deviceID]
      });
      toast.success(`Location tracking ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error("Error toggling location:", error);
      // Revert UI state if any API call fails
      setIsLocationEnabled(!enabled);
      toast.error("Failed to update location settings");
    }
  };
  
  const hasLocation = latitude !== null && longitude !== null && !isNaN(latitude) && !isNaN(longitude) && latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;

  return (
    <div className="p-2">
      <div className={`flex items-center justify-between mb-4 ${isMobile ? 'flex-col space-y-3' : ''}`}>
        <div className="flex items-center">
          <MapPin className="h-5 w-5 text-brand-500 mr-2" />
          <h2 className="text-lg font-semibold">Location Details</h2>
          {device.gnss?.gnssLastUpdated && (
            <span className="text-xs text-muted-foreground ml-2">
              (last updated: {new Date(device.gnss.gnssLastUpdated).toLocaleString('en-GB', { 
                hour: '2-digit', 
                minute: '2-digit', 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric',
                hour12: false
              }).replace(/(\d{2}:\d{2}), (\d{2}\/\d{2}\/\d{4})/, '$1 $2')})
            </span>
          )}
        </div>
        <div className={`flex items-center ${isMobile ? 'w-full justify-between mt-2' : 'gap-3'}`}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Enable</span>
                  <Switch 
                    checked={isLocationEnabled} 
                    onCheckedChange={handleToggleLocation} 
                    disabled={!canEditDeviceConfig}
                    aria-label="Toggle location tracking" 
                  />
                </div>
              </TooltipTrigger>
              {!canEditDeviceConfig && (
                <TooltipContent>
                  Only administrators can modify location settings
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleUpdateLocation} 
            disabled={isUpdating || !isLocationEnabled || !canEditDeviceConfig} 
            className="gap-2 text-black" 
            style={{ backgroundColor: '#DFEF97' }}
          >
            <RefreshCw className={`h-4 w-4 ${isUpdating ? 'animate-spin' : ''}`} />
            {isMobile ? 'Update' : 'Update Location'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setManualOpen(true)}
            disabled={!canEditDeviceConfig}
            className="gap-2"
          >
            <MapPinned className="h-4 w-4" />
            {isMobile ? 'Manual' : 'Set Manual Location'}
          </Button>
        </div>
      </div>

      {hasLocation && (isLocationEnabled || !gnssLatitude) ? (
        <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-1 md:grid-cols-2 gap-3'}`}>
          {/* Left Column - Map and Coordinates */}
          <div className="space-y-3">
            {/* Location Map */}
            <Card className="border bg-muted/50">
              <CardContent className="pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="h-4 w-4 text-brand-500" />
                  <h3 className="font-medium">Location Map</h3>
                </div>
                {hasLocation && <DeviceLocationMap latitude={latitude} longitude={longitude} deviceName={device.device_name} thumbnailUrl={device.imagekit?.thumbnailUrl} />}
              </CardContent>
            </Card>

            {/* Coordinates Card */}
            <Card className="border bg-muted/50">
              <CardContent className="pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <Navigation className="h-4 w-4 text-brand-500" />
                  <h3 className="font-medium">Coordinates</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">NMEA Latitude</p>
                    <p className="font-medium">
                      {device.gnss.latitude}° {device.gnss.northSouth}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Decimal Latitude</p>
                    <p className="font-medium">{latitude?.toFixed(6)}°</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">NMEA Longitude</p>
                    <p className="font-medium">
                      {device.gnss.longitude}° {device.gnss.eastWest}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Decimal Longitude</p>
                    <p className="font-medium">{longitude?.toFixed(6)}°</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Additional Info */}
          {(!isMobile || (isMobile && hasLocation)) && (
            <div className="space-y-3">
              {/* Altitude Card */}
              <Card className="border bg-muted/50">
                <CardContent className="pt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Compass className="h-4 w-4 text-brand-500" />
                    <h3 className="font-medium">Elevation Data</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Altitude</p>
                      <p className="font-medium">{device.gnss.altitude} meters</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Geoidal Separation</p>
                      <p className="font-medium">{device.gnss.geoidal} meters</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Signal Info Card */}
              <Card className="border bg-muted/50">
                <CardContent className="pt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Satellite className="h-4 w-4 text-brand-500" />
                    <h3 className="font-medium">{isMobile ? 'Signal Info' : 'Signal Information'}</h3>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Satellites</p>
                      <p className="font-medium">{device.gnss.satellites} in view</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">HDOP</p>
                      <p className="font-medium">{device.gnss.hdop}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Last Update</p>
                      <p className="font-medium">{device.gnss.utcTime || "N/A"}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Additional Space for Future Metrics */}
              <Card className="border bg-muted/50">
                <CardContent className="pt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Satellite className="h-4 w-4 text-brand-500" />
                    <h3 className="font-medium">GPS Quality</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Fix Quality</p>
                      <p className="font-medium">{device.gnss.fixQuality || "Standard"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">GPS Mode</p>
                      <p className="font-medium">{device.gnss.gpsMode || "3D"}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-6">
          <Satellite className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            {!isLocationEnabled ? "Location tracking is disabled. Enable it to view location data." : "No location data available. The device may need GPS enabled or might be searching for satellites."}
          </p>
        </div>
      )}

      <ManualLocationDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        device={device}
        initialLat={latitude}
        initialLng={longitude}
      />
    </div>
  );
};
